import type { FormEvent, ReactNode } from "react";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";

export interface ProjectTaskCreateDialogRenderProps {
  readonly projectTitle: string;
  readonly disabled: boolean;
  readonly title: string;
  readonly description: string;
  readonly headerAction: ReactNode;
  readonly planningFields: ReactNode;
  readonly moreProperties: ReactNode;
  readonly footer: ReactNode;
  readonly errors: ReactNode;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: () => void;
  readonly onTitleChange: (value: string) => void;
  readonly onDescriptionChange: (value: string) => void;
}

export function ProjectTaskCreateDialogRender(props: ProjectTaskCreateDialogRenderProps) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };
  return (
    <Dialog open onOpenChange={props.onOpenChange}>
      <DialogPopup className="max-w-4xl max-sm:max-h-[calc(100dvh-1rem)] max-sm:rounded-t-2xl max-sm:border-x">
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <DialogHeader
            className="shrink-0 gap-1 px-4 pb-3 pr-12 pt-4 md:px-6 md:pr-14 md:pt-6"
            data-task-create-header
          >
            <DialogTitle className="text-base">
              <span className="text-muted-foreground">{props.projectTitle}</span>
              <span className="px-2 text-muted-foreground" aria-hidden>
                ›
              </span>
              New task
            </DialogTitle>
            <DialogDescription className="max-w-2xl text-sm leading-5 text-muted-foreground">
              Create one task directly, or describe an outcome and choose Plan with Project Lead to
              open a durable planning conversation. Nothing is added to the board until you review
              the plan.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-4 px-4 pb-4 md:space-y-5 md:px-6">
            <div className="flex flex-wrap items-start gap-3" data-task-create-title-row>
              <Input
                autoFocus
                disabled={props.disabled}
                unstyled
                value={props.title}
                maxLength={500}
                onChange={(event) => props.onTitleChange(event.currentTarget.value)}
                placeholder="Task title"
                aria-label="New task title"
                className="min-h-11 min-w-0 flex-1 rounded-none px-0 text-xl font-semibold md:min-h-9 md:text-2xl"
              />
              <div className="ml-auto flex min-h-9 shrink-0 items-center justify-end">
                {props.headerAction}
              </div>
            </div>
            <MarkdownEditor
              disabled={props.disabled}
              value={props.description}
              maxLength={5_000}
              onChange={props.onDescriptionChange}
              ariaLabel="Task description"
              placeholder="Add description…"
              density="compact"
              className="[&_[contenteditable]]:min-h-20 [&_textarea]:min-h-20 md:[&_[contenteditable]]:min-h-36 md:[&_textarea]:min-h-36"
            />
            {props.planningFields}
            {props.moreProperties}
            {props.errors}
          </DialogPanel>
          {props.footer}
        </form>
      </DialogPopup>
    </Dialog>
  );
}

export function TaskCreateError(props: { readonly error: string | null }) {
  return props.error !== null ? (
    <p role="alert" className="text-sm text-destructive">
      {props.error}
    </p>
  ) : null;
}
